import React, { useState } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import { patchCustomer } from "../../../../../shared/services/services";
import { notifyError, notifySuccess } from "../../../../../assets/toastService";
import PopupModules from "src/features/auth/components/PopupModules";
import { textColorOptions } from "src/shared/modules/profile/theme/themeConstants";
import { useUser } from "src/shared/context/UserContext";
import { errorHandling } from "src/shared/services/errorHandling";
import InputGroup from "./InputGroup";
import { CustomerData } from "src/shared/types";
import { Plus, Trash2 } from "lucide-react";

type Props = {
  close: (data: any) => void;
  prvCustomer: CustomerData;
  loading: (data: boolean) => void;
  editCustomer: boolean;
};

const EditCustomer: React.FC<Props> = ({ close, prvCustomer, loading, editCustomer }) => {
  const { user } = useUser();

  // ================= STATE =================
  const [formData, setFormData] = useState({
    firstName: prvCustomer.firstName || "",
    middleName: prvCustomer.middleName || "",
    lastName: prvCustomer.lastName || "",

    phonesNumbers: prvCustomer.phones?.length
      ? prvCustomer.phones.map((p) => p.phone)
      : [""],

    // ✅ FIXED HERE
    emailIds: prvCustomer.emails?.length
      ? prvCustomer.emails.map((e) => e.email)
      : [],

    addressLine1: prvCustomer.addresses?.addressLine1 || "",
    addressLine2: prvCustomer.addresses?.addressLine2 || "",
    city: prvCustomer.addresses?.city || "",
    state: prvCustomer.addresses?.state || "",
    postalCode: prvCustomer.addresses?.postalCode || "",
  });

  const [primaryPhoneIndex, setPrimaryPhoneIndex] = useState(
    prvCustomer.phones?.findIndex((p) => p.isPrimary) ?? 0
  );

  const [primaryEmailIndex, setPrimaryEmailIndex] = useState(
    prvCustomer.emails?.findIndex((e) => e.isPrimary) ?? 0
  );

  const [showMore, setShowMore] = useState(false);
  const [showMiddleName, setShowMiddleName] = useState(!!prvCustomer.middleName);

  // ================= HANDLERS =================
  const handleChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleArrayChange = (
    index: number,
    value: string,
    field: "phonesNumbers" | "emailIds"
  ) => {
    let updatedValue = value;

    // Phone number validation
    if (field === "phonesNumbers") {
      // allow only numbers
      updatedValue = value.replace(/\D/g, "");

      // max length 12
      if (updatedValue.length > 12) return;

      // minimum length check can be used while submit
    }

    // Email validation
    if (field === "emailIds") {
      updatedValue = value.trim();

      // optional live validation
      const emailRegex =
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|in|co|net|org|edu|gov)$/i;

      if (
        updatedValue &&
        !emailRegex.test(updatedValue)
      ) {
        console.log("Invalid Email");
      }
    }

    const updated = [...formData[field]];
    updated[index] = updatedValue;

    setFormData((prev) => ({
      ...prev,
      [field]: updated,
    }));
  };

  const addField = (field: "phonesNumbers" | "emailIds") => {
    if (formData[field].length >= 2) {
      notifyError("Maximum 2 entries allowed");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: [...prev[field], ""],
    }));
  };

  const removeField = (index: number, field: "phonesNumbers" | "emailIds") => {
    const updated = formData[field].filter((_, i) => i !== index);

    setFormData((prev) => ({
      ...prev,
      [field]: field === "phonesNumbers" ? (updated.length ? updated : [""]) : updated,
    }));

    // Fix primary index
    if (field === "phonesNumbers") {
      if (primaryPhoneIndex === index) setPrimaryPhoneIndex(0);
      else if (primaryPhoneIndex > index) setPrimaryPhoneIndex((prev) => prev - 1);
    }

    if (field === "emailIds") {
      if (primaryEmailIndex === index) setPrimaryEmailIndex(0);
      else if (primaryEmailIndex > index) setPrimaryEmailIndex((prev) => prev - 1);
    }
  };

  const handleNumberKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["-", "e", "E", "+", ".", "(", ")"].includes(e.key)) e.preventDefault();
  };

  // ================= SUBMIT =================
  const handleConfirm = async () => {
    const validPhones = formData.phonesNumbers.filter((p) => p.trim());
    const validEmails = formData.emailIds.filter((e) => e.trim());

    if (!formData.firstName.trim()) {
      notifyError("First Name is required");
      return;
    }

    if (!validPhones.length) {
      notifyError("At least one phone number required");
      return;
    }

    const payload = {
      firstName: formData.firstName.trim(),
      middleName: formData.middleName.trim(),
      lastName: formData.lastName.trim(),

      phones: validPhones.map((phone, index) => ({
        phone,
        isPrimary: index === primaryPhoneIndex,
      })),

      emails: validEmails.map((email, index) => ({
        email,
        isPrimary: index === primaryEmailIndex,
      })),

      addresses: {
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        postalCode: formData.postalCode,
      },
    };

    try {
      loading(true);
      const res = await patchCustomer(prvCustomer._id.$oid, payload);
      notifySuccess("Customer updated successfully!");
      close(res);
    } catch (err) {
      errorHandling(err);
    } finally {
      loading(false);
    }
  };

  const isDisabled =
    !formData.firstName.trim() ||
    !formData.phonesNumbers[0] ||
    formData.phonesNumbers[0].length < 8;

  // ================= UI =================
  return (
    <PopupModules isOpen={editCustomer} onClose={() => close("")} title="Edit Customer">
      <div className="flex flex-col gap-3 p-3">

        {/* NAME */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <InputGroup label="First Name" value={formData.firstName} name="firstName" onChange={handleChange} mandatory />
          <InputGroup label="Last Name" value={formData.lastName} name="lastName" onChange={handleChange} />
        </div>

        {!showMiddleName && (
          <button
            className={`text-sm text-end font-medium ${textColorOptions[user?.themes.backgroundColour ?? "Blue"]}`}
            onClick={() => setShowMiddleName(true)}
          >
            + Middle Name
          </button>
        )}

        {showMiddleName && (
          <InputGroup label="Middle Name" value={formData.middleName} name="middleName" onChange={handleChange} />
        )}

        {/* PHONE */}
        <div>
          <p className="text-sm font-medium mb-1">Phone Numbers *</p>
          {formData.phonesNumbers.map((phone, index) => (
            <div key={index} className="flex gap-2 mb-2 items-center">

              {formData.phonesNumbers.length > 1 && (
                <input
                  type="radio"
                  checked={primaryPhoneIndex === index}
                  onChange={() => setPrimaryPhoneIndex(index)}
                />
              )}

              <input
                type="number"
                value={phone}
                onChange={(e) => handleArrayChange(index, e.target.value, "phonesNumbers")}
                onKeyDown={handleNumberKey}
                className="w-full border rounded px-2 py-2"
              />

              {formData.phonesNumbers.length < 2 && (
                <button onClick={() => addField("phonesNumbers")}>
                  <Plus size={18} />
                </button>
              )}

              {formData.phonesNumbers.length > 1 && (
                <button onClick={() => removeField(index, "phonesNumbers")}>
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* TOGGLE */}
        <div className="flex justify-end">
          <button
            onClick={() => setShowMore(!showMore)}
            className={`text-sm ${textColorOptions[user?.themes.backgroundColour ?? "Blue"]}`}
          >
            {showMore ? "Hide Details ▲" : "More Details ▼"}
          </button>
        </div>

        {/* MORE */}
        {showMore && (
          <div className="border-t pt-3">

            {/* EMAIL */}
            <div>
              <p className="text-sm font-medium mb-1">Email IDs</p>

              {formData.emailIds.length === 0 && (
                <button onClick={() => addField("emailIds")}>
                  <Plus size={18} /> Add Email
                </button>
              )}

              {formData.emailIds.map((email, index) => (
                <div key={index} className="flex gap-2 mb-2 items-center">

                  {formData.emailIds.length > 1 && (
                    <input
                      type="radio"
                      checked={primaryEmailIndex === index}
                      onChange={() => setPrimaryEmailIndex(index)}
                    />
                  )}

                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleArrayChange(index, e.target.value, "emailIds")}
                    className="w-full border rounded px-2 py-2"
                  />

                  {formData.emailIds.length < 2 && (
                    <button onClick={() => addField("emailIds")}>
                      <Plus size={18} />
                    </button>
                  )}

                  {formData.emailIds.length > 0 && (
                    <button onClick={() => removeField(index, "emailIds")}>
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* ADDRESS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <InputGroup label="Address Line 1" value={formData.addressLine1} name="addressLine1" onChange={handleChange} />
              <InputGroup label="Address Line 2" value={formData.addressLine2} name="addressLine2" onChange={handleChange} />
              <InputGroup label="City" value={formData.city} name="city" onChange={handleChange} />
              <InputGroup label="State" value={formData.state} name="state" onChange={handleChange} />
              <InputGroup label="Postal Code" value={formData.postalCode} name="postalCode" onChange={handleChange} />
            </div>

          </div>
        )}

        {/* BUTTONS */}
        <div className="flex justify-center gap-4 pt-4 border-t">
          <Button label="Cancel" variant="ThemeCancel" onClick={() => close("")} />
          <Button label="Confirm" variant="ThemeBase" onClick={handleConfirm} disabled={isDisabled} />
        </div>

      </div>
    </PopupModules>
  );
};

export default EditCustomer;